import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useDeleteDepartmentOverride,
  useUpsertDepartmentOverride,
} from "@/hooks/useSupportDepartments";
import type { AddedPerson, Department } from "@/services/supportDepartmentsService";

import { apiErrorMessage } from "./slackId";

function slackStatus(p: AddedPerson): { label: string; style: string } {
  if (p.slack_user_id) return { label: "Override", style: "border-coral/40 text-coral-dark" };
  if (p.effective_slack_id) return { label: "Linked", style: "border-teal/50 text-teal-dark" };
  return { label: "Missing", style: "border-destructive/40 text-destructive" };
}

/** Someone added to this department in Core. Their Slack id is the one set
 *  here, else their Teamwork row's, else the sync's lookup. */
export function AddedPersonRow({
  person,
  department,
}: {
  person: AddedPerson;
  department: Department;
}) {
  const upsert = useUpsertDepartmentOverride();
  const remove = useDeleteDepartmentOverride();
  const busy = upsert.isPending || remove.isPending;
  const error = upsert.error ?? remove.error;
  const status = slackStatus(person);

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
      <div className="min-w-[10rem] flex-1">
        <p className="text-sm font-medium text-foreground">{person.name}</p>
        <p className="text-xs text-muted-foreground">{person.email}</p>
      </div>
      {person.is_lead ? (
        <Badge variant="outline" className="border-coral/40 text-coral">Lead (Core)</Badge>
      ) : null}
      <Badge variant="outline" className="border-coral/40 text-coral-dark">Added in Core</Badge>
      <Badge variant="outline" className={status.style}>
        {status.label}
      </Badge>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={busy}
        aria-label={person.is_lead ? `Remove ${person.name} as lead` : `Make ${person.name} lead`}
        onClick={() =>
          upsert.mutate({
            department: department.key,
            input: {
              email: person.email,
              display_name: person.display_name,
              mode: "include",
              is_lead: !person.is_lead,
              slack_user_id: person.slack_user_id,
            },
          })
        }
      >
        {person.is_lead ? "Remove lead" : "Make lead"}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={busy}
        aria-label={`Remove ${person.name} from ${department.label}`}
        onClick={() => remove.mutate({ department: department.key, email: person.email })}
      >
        Remove
      </Button>
      {error ? (
        <p role="alert" className="w-full text-xs text-destructive">
          {apiErrorMessage(error, "the change was not saved.")}
        </p>
      ) : null}
    </li>
  );
}
